import { IWizardStep, IWizardStepState, WizardManager } from 'fitbeat.gym-core';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { createAppContainer, NavigationActions } from 'react-navigation';
import { createStackNavigator } from 'react-navigation-stack';

interface IScreenComponentProps {
  navigation: any;
  getNextStep(): void;
  getPreviousStep(): void;
}
interface IWizardUiStep<Step> extends IWizardStep<Step> {
  ScreenComponent: React.FunctionComponent<IScreenComponentProps>;
}

interface IProps<Step, Answer> {
  wizardManager: WizardManager<Step, Answer>;
  steps: IWizardUiStep<Step>[];
  isAnimationDisabled: boolean;
}

interface IState<Step, Answer> {
  wizardStepState: IWizardStepState<Step, Answer>;
}

export class Wizard<Step, Answer> extends React.Component<
  IProps<Step, Answer>,
  IState<Step, Answer>
> {
  private StackNavigator: any;
  private navigator: any;
  constructor(props) {
    super(props);
    const { steps } = this.props;
    this.state = {
      wizardStepState: {
        currentStep: steps[0],
        steps,
        answers: {},
      },
    };
    this.createStackNavigator();
  }

  public componentDidUpdate(
    prevProps,
    preState: Readonly<IState<Step, Answer>>,
  ): void {
    this.handleSteps(prevProps);
    this.handleNavigation(preState);
  }

  public next = () => {
    const { wizardStepState } = this.state;
    const currentStep = this.props.wizardManager.getNextStep(wizardStepState);
    if (currentStep) {
      this.setState({ wizardStepState: { ...wizardStepState, currentStep } });
    }
  }

  public previous = () => {
    const { wizardStepState } = this.state;
    const currentStep =
      this.props.wizardManager.getPreviousStep(wizardStepState);
    if (currentStep) {
      this.setState({ wizardStepState: { ...wizardStepState, currentStep } });
    }
  }

  public render() {
    const { StackNavigator } = this;
    return (
      <View style={styles.container}>
        <StackNavigator
          ref={(nav) => {
            this.navigator = nav;
          }}
        />
      </View>
    );
  }

  private createStackNavigator = () => {
    const { steps, isAnimationDisabled = false } = this.props;
    const screens = steps.reduce((acc, { route, ScreenComponent }) => {
      acc[route] = {
        screen: ({ navigation }) => {
          return (
            <ScreenComponent
              getNextStep={this.next}
              getPreviousStep={this.previous}
              navigation={navigation}
            />
          );
        },
      };
      return acc;
    }, {});
    this.StackNavigator = createAppContainer(
      createStackNavigator(screens, {
        initialRouteName: steps[0].route,
        defaultNavigationOptions: {
          animationEnabled: isAnimationDisabled,
        },
      }),
    );
  }
  private handleNavigation(prevState: IState<Step, Answer>) {
    const {
      wizardStepState: { currentStep },
    } = this.state;
    const {
      wizardStepState: { currentStep: prevStep },
    } = prevState;
    const prevRoute = prevStep?.route;
    const currentRoute = currentStep?.route;
    const shouldNavigate = currentRoute && prevRoute !== currentRoute;
    if (shouldNavigate) {
      this.navigator.dispatch(
        NavigationActions.navigate({ routeName: currentRoute }),
      );
    }
  }

  private handleSteps = (prevProps: IProps<Step, Answer>) => {
    if (this.shouldRebuildSteps(prevProps)) {
      this.createStackNavigator();
      this.setState((prevState) => {
        return {
          wizardStepState: {
            ...prevState.wizardStepState,
            steps: this.props.steps,
          },
        };
      });
    }
  }

  private shouldRebuildSteps = (prevProps: IProps<Step, Answer>) => {
    if (prevProps.steps.length !== this.props.steps.length) {
      return true;
    }
    let shouldReturn = false;
    prevProps.steps.forEach(({ route }, index) => {
      if (this.props.steps[index].route !== route) {
        shouldReturn = true;
        return;
      }
    });
    return shouldReturn;
  }
}
const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
